import { useState, useEffect, useCallback } from 'react';

/**
 * useFilters - Custom hook for managing filter state with debouncing
 * 
 * @param {Object} initialFilters - Initial filter values
 * @param {Function} onFiltersChange - Callback when filters change (optional)
 * @param {Number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns {Object} { filters, updateFilter, resetFilters, debouncedFilters }
 */
const useFilters = (initialFilters = {}, onFiltersChange, debounceMs = 300) => {
    const defaultFilters = {
        search: '',
        zipcode: '',
        categories: [],
        brands: [],
        rating: 0,
        priceRange: [0, 100000],
        ...initialFilters,
    };

    const [filters, setFilters] = useState(defaultFilters);
    const [debouncedFilters, setDebouncedFilters] = useState(defaultFilters);

    // Debounce effect for search inputs
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedFilters(filters);
            if (onFiltersChange) {
                onFiltersChange(filters);
            }
        }, debounceMs);

        return () => clearTimeout(timeoutId);
    }, [filters, debounceMs, onFiltersChange]);

    // Update a single filter
    const updateFilter = useCallback((filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value,
        }));
    }, []);

    // Update multiple filters at once
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters,
        }));
    }, []);

    // Reset all filters to initial state
    const resetFilters = useCallback(() => {
        setFilters(defaultFilters);
        setDebouncedFilters(defaultFilters);
    }, []);

    return {
        filters,
        debouncedFilters,
        updateFilter,
        updateFilters,
        resetFilters,
    };
};

export default useFilters;
